import { useEffect, useRef } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import Logo from '../components/Logo'
import { track, Events } from '../lib/analytics'

const SCROLL_THRESHOLDS = [25, 50, 75, 100]
const SCROLL_SESSION_KEY = 'fp_landing_depth_fired'

function trackCta(location, variant) {
  track(Events.LandingCtaClicked, { location, variant })
}

function useScrollDepthTracking() {
  useEffect(() => {
    const fired = new Set()
    try {
      const saved = sessionStorage.getItem(SCROLL_SESSION_KEY)
      if (saved) JSON.parse(saved).forEach(t => fired.add(Number(t)))
    } catch { /* ignore */ }

    function persist() {
      try { sessionStorage.setItem(SCROLL_SESSION_KEY, JSON.stringify([...fired])) } catch { /* ignore */ }
    }

    let ticking = false
    function measure() {
      ticking = false
      const doc = document.documentElement
      const scrollable = doc.scrollHeight - window.innerHeight
      // Short pages (content fits within a single viewport) count as 100%
      // the moment they're rendered — otherwise they'd never fire.
      const pct = scrollable <= 0
        ? 100
        : Math.min(100, ((window.scrollY) / scrollable) * 100)
      for (const t of SCROLL_THRESHOLDS) {
        if (pct >= t && !fired.has(t)) {
          fired.add(t)
          track(Events.LandingScrollDepth, { depth_percent: String(t) })
          persist()
        }
      }
    }

    function onScroll() {
      if (ticking) return
      ticking = true
      window.requestAnimationFrame(measure)
    }

    window.addEventListener('scroll', onScroll, { passive: true })
    window.addEventListener('resize', measure, { passive: true })
    // Fire once on mount so short pages (or refreshes at the bottom) count.
    measure()

    return () => {
      window.removeEventListener('scroll', onScroll)
      window.removeEventListener('resize', measure)
    }
  }, [])
}

function WheelVisual() {
  return (
    <div
      className="relative select-none"
      style={{
        width: 'min(360px, 80vw)',
        aspectRatio: '1',
      }}
      aria-hidden="true"
    >
      {/* Outer glow */}
      <div
        className="absolute inset-0 rounded-full"
        style={{
          background:
            'radial-gradient(circle at center, rgba(200,16,46,0.35) 0%, rgba(200,16,46,0.0) 65%)',
          filter: 'blur(8px)',
        }}
      />
      {/* Spinning wheel */}
      <svg
        viewBox="0 0 200 200"
        className="relative w-full h-full"
        style={{
          animation: 'wheel-loop 14s linear infinite',
          transformOrigin: 'center',
          filter: 'drop-shadow(0 20px 50px rgba(0,0,0,0.6))',
        }}
      >
        {/* 8 alternating red/gold segments */}
        {Array.from({ length: 8 }).map((_, i) => {
          const angle = (Math.PI * 2) / 8
          const start = i * angle - Math.PI / 2
          const end = start + angle
          const x1 = 100 + 95 * Math.cos(start)
          const y1 = 100 + 95 * Math.sin(start)
          const x2 = 100 + 95 * Math.cos(end)
          const y2 = 100 + 95 * Math.sin(end)
          const fill = i % 2 === 0 ? 'var(--fp-red)' : 'var(--fp-gold)'
          return (
            <path
              key={i}
              d={`M100,100 L${x1},${y1} A95,95 0 0,1 ${x2},${y2} Z`}
              fill={fill}
              stroke="#0a0a0a"
              strokeWidth="1"
            />
          )
        })}
        {/* Hub */}
        <circle cx="100" cy="100" r="18" fill="#0a0a0a" stroke="var(--fp-gold)" strokeWidth="2" />
        <circle cx="100" cy="100" r="6" fill="var(--fp-gold)" />
      </svg>

      {/* Pointer — stays still, the wheel rotates under it */}
      <svg
        className="absolute"
        viewBox="0 0 40 40"
        style={{
          top: '-6px',
          left: '50%',
          transform: 'translateX(-50%)',
          width: '36px',
          height: '36px',
          filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.8))',
        }}
        aria-hidden="true"
      >
        <polygon points="20,32 6,6 34,6" fill="var(--fp-gold)" stroke="#0a0a0a" strokeWidth="1.5" />
      </svg>

      <style>{`
        @keyframes wheel-loop {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        @media (prefers-reduced-motion: reduce) {
          svg[style*="wheel-loop"] { animation: none !important; }
        }
      `}</style>
    </div>
  )
}

function Step({ number, title, body }) {
  return (
    <div className="flex-1 flex flex-col items-start">
      <div
        className="flex items-center justify-center text-base font-bold mb-6 rounded-full"
        style={{
          width: '44px',
          height: '44px',
          background: 'var(--fp-gold-soft)',
          color: 'var(--fp-gold)',
          letterSpacing: '0',
        }}
      >
        {number}
      </div>
      <h3 className="fp-display text-2xl text-white mb-3" style={{ letterSpacing: '-0.02em' }}>
        {title}
      </h3>
      <p className="text-base text-[var(--fp-text-muted)]" style={{ lineHeight: '1.6' }}>
        {body}
      </p>
    </div>
  )
}

function Feature({ title, body }) {
  return (
    <div className="fp-card" style={{ padding: '32px' }}>
      <h3 className="fp-display text-2xl text-white mb-3" style={{ letterSpacing: '-0.02em' }}>
        {title}
      </h3>
      <p className="text-base text-[var(--fp-text-muted)]" style={{ lineHeight: '1.6' }}>
        {body}
      </p>
    </div>
  )
}

export default function Landing() {
  const navigate = useNavigate()
  const navRef = useRef(null)
  useScrollDepthTracking()

  function goSignup(location) {
    trackCta(location, 'Join the crew')
    navigate('/signup')
  }

  function goLogin(location) {
    trackCta(location, 'Sign in')
    navigate('/login')
  }

  return (
    <div
      className="min-h-[100dvh] w-full"
      style={{ background: 'var(--fp-bg)', color: 'var(--fp-text)' }}
    >
      {/* Sticky nav */}
      <nav
        ref={navRef}
        className="sticky top-0 z-40 w-full"
        style={{
          background: 'rgba(0, 0, 0, 0.72)',
          backdropFilter: 'saturate(140%) blur(10px)',
          WebkitBackdropFilter: 'saturate(140%) blur(10px)',
          borderBottom: '1px solid var(--fp-border)',
        }}
      >
        <div
          className="mx-auto flex items-center justify-between px-5 sm:px-8"
          style={{ maxWidth: '1200px', height: '64px' }}
        >
          <Logo size="md" />
          <button
            onClick={() => goLogin('nav')}
            className="text-sm font-semibold bg-transparent border-none cursor-pointer px-3 py-2 rounded-lg hover:text-white transition-colors"
            style={{ color: 'var(--fp-text-muted)', minHeight: '44px' }}
          >
            Sign in
          </button>
        </div>
      </nav>

      {/* Hero */}
      <section
        className="relative w-full flex items-center"
        style={{ minHeight: 'calc(100dvh - 64px)' }}
      >
        {/* Ambient red glow */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              'radial-gradient(ellipse 80% 60% at 70% 40%, rgba(200,16,46,0.14) 0%, transparent 70%)',
          }}
        />

        <div
          className="relative mx-auto w-full px-6 sm:px-10 py-16 sm:py-24 flex flex-col-reverse lg:flex-row items-center gap-12 lg:gap-16"
          style={{ maxWidth: '1200px' }}
        >
          {/* Copy */}
          <div className="flex-1 w-full text-center lg:text-left">
            <div
              className="text-xs fp-gold-text uppercase mb-5 font-semibold"
              style={{ letterSpacing: '0.3em' }}
            >
              For crews who can't agree on anything.
            </div>
            <h1
              className="fp-display text-white mb-6"
              style={{
                fontSize: 'clamp(2.75rem, 7vw, 5rem)',
                lineHeight: '1.02',
                letterSpacing: '-0.04em',
              }}
            >
              Someone has to decide.
            </h1>
            <p
              className="mx-auto lg:mx-0 mb-10"
              style={{
                color: 'var(--fp-text-muted)',
                fontSize: 'clamp(1rem, 1.6vw, 1.25rem)',
                lineHeight: '1.55',
                maxWidth: '540px',
              }}
            >
              Everyone picks one movie. The wheel chooses. Movie night in under a minute.
            </p>
            <div className="flex flex-col items-center lg:items-start gap-3">
              <button
                onClick={() => goSignup('hero')}
                className="fp-btn fp-btn-primary plausible-event-name=Landing+CTA+Clicked plausible-event-location=hero plausible-event-variant=Join+the+crew"
                style={{
                  width: 'auto',
                  minHeight: '44px',
                  padding: '20px 40px',
                  fontSize: '17px',
                  boxShadow: '0 12px 40px var(--fp-red-glow)',
                }}
              >
                Join the crew
              </button>
              <button
                onClick={() => goLogin('hero')}
                className="text-sm font-semibold bg-transparent border-none cursor-pointer"
                style={{ color: 'var(--fp-text-muted)', minHeight: '44px', padding: '10px 0' }}
              >
                Already have a crew?{' '}
                <span style={{ color: 'var(--fp-red)' }}>Sign in</span>
              </button>
            </div>
          </div>

          {/* Visual */}
          <div className="flex-1 w-full flex items-center justify-center">
            <WheelVisual />
          </div>
        </div>
      </section>

      {/* How it works */}
      <section
        id="how-it-works"
        className="w-full"
        style={{
          background: 'linear-gradient(180deg, var(--fp-bg), var(--fp-surface))',
          paddingTop: 'clamp(80px, 10vw, 120px)',
          paddingBottom: 'clamp(80px, 10vw, 120px)',
        }}
      >
        <div
          className="mx-auto px-6 sm:px-10"
          style={{ maxWidth: '1200px' }}
        >
          <div
            className="text-xs fp-gold-text uppercase mb-4 font-semibold text-center"
            style={{ letterSpacing: '0.3em' }}
          >
            How it works
          </div>
          <h2
            className="fp-display text-white text-center mb-16 mx-auto"
            style={{
              fontSize: 'clamp(2rem, 4.5vw, 3rem)',
              letterSpacing: '-0.03em',
              maxWidth: '760px',
            }}
          >
            Three steps. Ninety seconds. No more debate.
          </h2>
          <div className="flex flex-col lg:flex-row gap-10 lg:gap-14">
            <Step
              number="1"
              title="Gather your crew."
              body="Share a link. They sign in. Done."
            />
            <Step
              number="2"
              title="Everyone picks one movie."
              body="No arguing. No scrolling. One pick each."
            />
            <Step
              number="3"
              title="The wheel decides."
              body="Spin it. No take-backs. Watch the movie."
            />
          </div>
        </div>
      </section>

      {/* Why FlickPick */}
      <section
        id="why-flickpick"
        className="w-full"
        style={{
          paddingTop: 'clamp(80px, 10vw, 120px)',
          paddingBottom: 'clamp(80px, 10vw, 120px)',
        }}
      >
        <div
          className="mx-auto px-6 sm:px-10"
          style={{ maxWidth: '1200px' }}
        >
          <div
            className="text-xs fp-gold-text uppercase mb-4 font-semibold text-center"
            style={{ letterSpacing: '0.3em' }}
          >
            Why FlickPick
          </div>
          <h2
            className="fp-display text-white text-center mb-16 mx-auto"
            style={{
              fontSize: 'clamp(2rem, 4.5vw, 3rem)',
              letterSpacing: '-0.03em',
              maxWidth: '760px',
            }}
          >
            Because "what should we watch?" shouldn't take longer than the movie.
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Feature
              title="No more scrolling wars."
              body="The average household spends 20 minutes picking what to watch. FlickPick takes 90 seconds."
            />
            <Feature
              title="It's fair. It's final."
              body="Everyone gets one vote. The wheel settles it. The loudest person doesn't win by default."
            />
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section
        className="w-full relative overflow-hidden"
        style={{
          paddingTop: 'clamp(80px, 10vw, 120px)',
          paddingBottom: 'clamp(80px, 10vw, 120px)',
          background:
            'radial-gradient(ellipse at center, rgba(200,16,46,0.18) 0%, var(--fp-bg) 70%)',
        }}
      >
        <div
          className="mx-auto px-6 sm:px-10 text-center"
          style={{ maxWidth: '760px' }}
        >
          <h2
            className="fp-display text-white mb-10 mx-auto"
            style={{
              fontSize: 'clamp(2.25rem, 5vw, 3.5rem)',
              letterSpacing: '-0.03em',
              lineHeight: '1.05',
            }}
          >
            Your next movie night starts here.
          </h2>
          <button
            onClick={() => goSignup('final-cta')}
            className="fp-btn fp-btn-primary mx-auto"
            style={{
              width: 'auto',
              minHeight: '44px',
              padding: '22px 52px',
              fontSize: '18px',
              boxShadow: '0 14px 48px var(--fp-red-glow)',
            }}
          >
            Join the crew
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer
        className="w-full"
        style={{
          background: 'var(--fp-surface)',
          borderTop: '1px solid var(--fp-border)',
          paddingTop: '40px',
          paddingBottom: 'calc(40px + env(safe-area-inset-bottom, 0px))',
        }}
      >
        <div
          className="mx-auto px-6 sm:px-10 flex flex-col sm:flex-row items-center justify-between gap-6"
          style={{ maxWidth: '1200px' }}
        >
          <div className="flex items-center gap-3 text-sm" style={{ color: 'var(--fp-text-muted)' }}>
            <Logo size="sm" />
            <span>© 2026 FlickPick</span>
          </div>
          <div
            className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-sm"
            style={{ color: 'var(--fp-text-muted)' }}
          >
            <Link to="/privacy" className="no-underline hover:text-white transition-colors" style={{ color: 'inherit' }}>
              Privacy
            </Link>
            <span aria-hidden="true">·</span>
            <Link to="/terms" className="no-underline hover:text-white transition-colors" style={{ color: 'inherit' }}>
              Terms
            </Link>
            <span aria-hidden="true">·</span>
            <Link to="/delete-data" className="no-underline hover:text-white transition-colors" style={{ color: 'inherit' }}>
              Data Deletion
            </Link>
            <span aria-hidden="true">·</span>
            <a
              href="mailto:hello@flickpick.com.au"
              className="no-underline hover:text-white transition-colors"
              style={{ color: 'inherit' }}
            >
              hello@flickpick.com.au
            </a>
          </div>
        </div>
      </footer>
    </div>
  )
}
